import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { forgotPasswordInputSchema } from '@castinflow/shared';
import { Alert, Button, Field, Input } from '@castinflow/ui';
import { ApiError, apiFetch } from '../lib/api';
import { useSystemMailReadiness } from '../lib/system-mail';
import { usePageMeta } from '../shell/page-meta';
import { PublicCard } from '../shell/PublicLayout';
import { tr } from '../i18n/tr';
import { LinkNotice, LinkPageTitle } from './link-page-parts';

const LOGIN_LINK_CLASS =
  'text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus';

/**
 * "Parolamı unuttum" (M7 T2) — oturumsuz sayfa. Çalışma alanı adresi + e-posta alır ve sıfırlama bağlantısı ister
 * (`POST /auth/password/forgot`).
 *
 * Hesabın varlığı SIZMAZ: sunucu her durumda aynı yanıtı verir; sayfa da hesap var/yok, etkin/askıda ayrımı yapmadan TEK
 * metin gösterir ("eşleşen etkin bir hesap varsa…"). Hız sınırı (IP başına) ve biçim hatası hesaba bağlı değildir.
 *
 * Posta yapılandırılmamışsa bu baştan söylenir ve form gösterilmez: bilgi platform düzeyindedir (`/ready`, herkese açık),
 * girilen adrese bağlı değildir — hesap varlığı hakkında bir şey söylemez. Aksi hâlde sayfa "gönderilir" derdi ve ileti hiç
 * çıkmazdı. Durum okunamadıysa varsayımda bulunulmaz, form gösterilir.
 */
export function ForgotPasswordPage() {
  usePageMeta(tr.auth.forgotTitle);
  const mail = useSystemMailReadiness();
  const [tenantSlug, setTenantSlug] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = forgotPasswordInputSchema.safeParse({
      tenantSlug: tenantSlug.trim().toLowerCase(),
      email,
    });
    if (!parsed.success) {
      setError(tr.auth.forgotInvalid);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiFetch<unknown>('/auth/password/forgot', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(parsed.data),
      });
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'RATE_LIMITED'
          ? tr.auth.rateLimited
          : tr.auth.linkError,
      );
    } finally {
      setBusy(false);
    }
  }

  const mode = mail.data?.mode;

  return (
    <PublicCard>
      <div className="space-y-4" data-testid="forgot-page">
        <LinkPageTitle>{tr.auth.forgotTitle}</LinkPageTitle>
        {/* Sınama kipi (yalnız üretim dışı): istek alınır ama ileti gerçek alıcıya gitmez — istekten önce de sonra da görünür */}
        {mode === 'sink' ? (
          <div data-testid="forgot-mail-sink">
            <Alert tone="info" title={tr.auth.forgotMailSinkTitle}>
              {tr.auth.forgotMailSinkBody}
            </Alert>
          </div>
        ) : null}
        {mode === 'unconfigured' ? (
          <LinkNotice
            testId="forgot-mail-off"
            tone="warning"
            title={tr.auth.forgotMailOffTitle}
            body={tr.auth.forgotMailOffBody}
          />
        ) : done ? (
          <LinkNotice
            testId="forgot-done"
            tone="success"
            title={tr.auth.forgotDoneTitle}
            body={tr.auth.forgotDoneBody}
            action={
              <Button
                onClick={() => {
                  setDone(false);
                  setEmail('');
                }}
                data-testid="forgot-again"
              >
                {tr.auth.forgotAgain}
              </Button>
            }
          />
        ) : (
          <form
            onSubmit={(e) => void submit(e)}
            className="space-y-4"
            noValidate
            data-testid="forgot-form"
          >
            <p className="text-base text-fg-muted">{tr.auth.forgotIntro}</p>
            <Field label={tr.auth.workspaceAddress} required>
              <Input
                value={tenantSlug}
                onChange={(e) => {
                  setTenantSlug(e.target.value);
                }}
                autoComplete="organization"
                autoCapitalize="none"
                spellCheck={false}
                data-testid="forgot-slug"
              />
            </Field>
            <Field label={tr.auth.email} required>
              <Input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                autoComplete="username"
                data-testid="forgot-email"
              />
            </Field>
            {error === null ? null : (
              <div data-testid="forgot-error">
                <Alert tone="danger" title={error} announce />
              </div>
            )}
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              loading={busy}
              data-testid="forgot-submit"
            >
              {tr.auth.forgotSubmit}
            </Button>
            <p className="text-sm text-fg-muted">
              <Link to="/login" className={LOGIN_LINK_CLASS} data-testid="forgot-login">
                {tr.auth.backToLogin}
              </Link>
            </p>
          </form>
        )}
      </div>
    </PublicCard>
  );
}
